import React from 'react';
import { KPIMetrics } from '../../types';
import { getActualColor, getBillableRateColor } from '../../utils/colorHelpers';

interface OverallPerformanceWidgetProps {
  kpiMetrics: KPIMetrics;
  isLoading: boolean;
}

const LoadingCard = () => (
  <div className="flex flex-col items-center justify-center space-y-2 py-4">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
    <p className="text-xs text-slate-600">Loading...</p>
  </div>
);

export const OverallPerformanceWidget: React.FC<OverallPerformanceWidgetProps> = ({ kpiMetrics, isLoading }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {/* Budget total month */}
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md border border-slate-200/60 p-4 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Budget total month</h3>
          <div className="w-8 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
        </div>
        {isLoading ? <LoadingCard /> : (
          <p className="font-bold text-slate-900 whitespace-nowrap" style={{ fontSize: '22px' }}>
            {kpiMetrics.budgetTotalMonth !== null && kpiMetrics.budgetTotalMonth !== undefined ? `${Math.round(kpiMetrics.budgetTotalMonth).toLocaleString('no-NO')} kr` : 'N/A'}
          </p>
        )}
      </div>

      {/* Budget as of yesterday */}
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md border border-slate-200/60 p-4 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Budget as of yesterday</h3>
          <div className="w-8 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
        </div>
        {isLoading ? <LoadingCard /> : (
          <p className="font-bold text-slate-900 whitespace-nowrap" style={{ fontSize: '22px' }}>
            {kpiMetrics.budgetAsOfYesterday !== null && kpiMetrics.budgetAsOfYesterday !== undefined ? `${Math.round(kpiMetrics.budgetAsOfYesterday).toLocaleString('no-NO')} kr` : 'N/A'}
          </p>
        )}
      </div>

      {/* Actual as of yesterday */}
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md border border-slate-200/60 p-4 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Actual as of yesterday</h3>
          <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        {isLoading ? <LoadingCard /> : (
          <p className={`font-bold whitespace-nowrap ${getActualColor(kpiMetrics.actualAsOfYesterday || 0, kpiMetrics.budgetAsOfYesterday || 0)}`} style={{ fontSize: '22px' }}>
            {kpiMetrics.actualAsOfYesterday !== null && kpiMetrics.actualAsOfYesterday !== undefined ? `${Math.round(kpiMetrics.actualAsOfYesterday).toLocaleString('no-NO')} kr` : 'N/A'}
          </p>
        )}
      </div>

      {/* Budget billable rate */}
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md border border-slate-200/60 p-4 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Budget billable rate</h3>
          <div className="w-8 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
        </div>
        {isLoading ? <LoadingCard /> : (
          <p className="font-bold text-slate-900 whitespace-nowrap" style={{ fontSize: '22px' }}>
            {kpiMetrics.budgetBillableRate !== null && kpiMetrics.budgetBillableRate !== undefined ? `${Math.round(kpiMetrics.budgetBillableRate)}%` : 'N/A'}
          </p>
        )}
      </div>

      {/* Billable rate so far */}
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md border border-slate-200/60 p-4 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Billable rate so far</h3>
          <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </div>
        {isLoading ? <LoadingCard /> : (
          <p className={`font-bold whitespace-nowrap ${getBillableRateColor(kpiMetrics.billableRateSoFar || 0, kpiMetrics.budgetBillableRate || 0)}`} style={{ fontSize: '22px' }}>
            {kpiMetrics.billableRateSoFar !== null && kpiMetrics.billableRateSoFar !== undefined ? `${Math.round(kpiMetrics.billableRateSoFar * 100)}%` : 'N/A'}
          </p>
        )}
      </div>
    </div>
  );
};

export default OverallPerformanceWidget;
